import { useCallback, useEffect, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import * as adminApi from '../../api/admin';
import type { WorkerConfig } from '../../api/admin';
import { getSystemStatus } from '../../api/systemStatus';
import type { ServiceHealth, ServiceStatus } from '../../api/systemStatus';
import { extractErrorMessage } from '../../api/errors';
import { JobTimingsCard } from '../../components/JobTimingsCard';
import { SortHeader } from '../../components/SortHeader';
import { useSortedRows } from '../../util/tableSort';
import { useNumericSetting, type NumericSetting } from './useNumericSetting';

/**
 * Collapsible "Deployment instructions" — the podman/kubectl commands most
 * operators don't need day-to-day, tucked behind a disclosure so the page
 * reads as a control surface, not a runbook. Platform engineers expand it.
 */
function DeployInstructions({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Button
        size="small"
        onClick={() => setOpen((o) => !o)}
        endIcon={
          <ExpandMoreIcon
            sx={{
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s',
            }}
          />
        }
        sx={{ px: 0.5 }}
      >
        Deployment instructions
      </Button>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {children}
        </Stack>
      </Collapse>
    </Box>
  );
}

/** Monospace command block inside a DeployInstructions disclosure. */
function DeployCommand({ children }: { children: ReactNode }) {
  return (
    <Box
      component="pre"
      sx={{
        bgcolor: 'action.hover',
        p: 1.5,
        borderRadius: 1,
        fontSize: 13,
        m: 0,
        whiteSpace: 'pre-wrap',
      }}
    >
      {children}
    </Box>
  );
}

/**
 * One editable numeric setting card (title + number field + Save + collapsible
 * deploy instructions + success/error alerts). Both the replica-count and the
 * api-process forms are the same shape, so they share this — the differences
 * (label, range, description, deploy commands, the "running N of M" suffix)
 * come in as props. `ready` gates the body on the config having loaded;
 * `loading` shows a spinner in that gap (replica card only, preserving the
 * original per-card behaviour).
 */
function WorkerSettingCard({
  title,
  description,
  fieldLabel,
  min,
  max,
  setting,
  ready,
  loading = false,
  rowSuffix,
  deploy,
}: {
  title: string;
  description?: string;
  fieldLabel: string;
  min: number;
  max: number;
  setting: NumericSetting;
  ready: boolean;
  loading?: boolean;
  rowSuffix?: ReactNode;
  deploy: ReactNode;
}) {
  const showError = !setting.valid && setting.draft.length > 0;
  return (
    <Card>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {title}
        </Typography>
        <Divider sx={{ my: 1 }} />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {ready && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}

            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                type="number"
                label={fieldLabel}
                inputProps={{ min, max }}
                value={setting.draft}
                onChange={(e) => setting.setDraft(e.target.value)}
                disabled={setting.saving}
                sx={{ maxWidth: 160 }}
                error={showError}
                helperText={
                  showError
                    ? `Must be a whole number between ${min} and ${max}`
                    : ' '
                }
              />
              <Button
                variant="contained"
                onClick={() => void setting.save()}
                disabled={!setting.canSave}
              >
                {setting.saving ? 'Saving…' : 'Save'}
              </Button>
              {rowSuffix}
            </Stack>

            <DeployInstructions>{deploy}</DeployInstructions>

            {setting.error && <Alert severity="error">{setting.error}</Alert>}
            {setting.savedAt !== null && !setting.error && (
              <Alert severity="success">
                Saved. Run the command above to apply.
              </Alert>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Workers — insights-worker scale + recycle (ROADMAP #43.5).
 *
 * Two cards:
 *
 *   1. Desired replica count — superadmin sets the intended count
 *      (1–16) in `platform_settings`. The API doesn't drive the
 *      orchestrator; an operator scales actual replicas via compose
 *      (`podman-compose ... --scale insights-worker=N`) or k8s
 *      (`kubectl scale deployment insights-worker --replicas=N`).
 *
 *   2. Live workers — one row per replica from /admin/system-status
 *      (filtered to `insights-worker:*`), with a Recycle button that
 *      deletes the heartbeat row. Useful for clearing ghosts; an
 *      actually-alive worker re-registers on its next tick.
 */

function statusChipColor(s: ServiceStatus): 'success' | 'warning' | 'error' {
  if (s === 'ok') return 'success';
  if (s === 'stale') return 'warning';
  return 'error';
}

function parseInstance(service: ServiceHealth): string | null {
  // System Status names worker rows "insights-worker:<instance_id>";
  // anything else (incl. the no-replicas placeholder "insights-worker")
  // doesn't map to a recyclable row.
  const idx = service.name.indexOf(':');
  if (idx === -1) return null;
  return service.name.slice(idx + 1);
}

const NO_WORKERS: ServiceHealth[] = [];

const WORKER_SORT = {
  instance: (w: ServiceHealth) => parseInstance(w) ?? w.name,
  status: (w: ServiceHealth) => w.status,
  version: (w: ServiceHealth) => w.version,
  // Worker rows carry the heartbeat age (ms) in response_ms; `detail` is
  // just its rendered form ("last heartbeat 12 s ago").
  heartbeat: (w: ServiceHealth) => w.response_ms,
};

export function Workers({ embedded = false }: { embedded?: boolean } = {}) {
  const [config, setConfig] = useState<WorkerConfig | null>(null);
  const [workers, setWorkers] = useState<ServiceHealth[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Recycle action state.
  const [recyclingId, setRecyclingId] = useState<string | null>(null);
  const [recycleError, setRecycleError] = useState<string | null>(null);

  const { sort, sorted: sortedWorkers } = useSortedRows(
    workers ?? NO_WORKERS,
    WORKER_SORT,
    'instance',
  );

  const loadAll = useCallback(async () => {
    setLoadError(null);
    try {
      const [cfg, snap] = await Promise.all([
        adminApi.getWorkerConfig(),
        getSystemStatus(),
      ]);
      setConfig(cfg);
      setWorkers(
        snap.services.filter((s) => s.name.startsWith('insights-worker')),
      );
    } catch (err) {
      setLoadError(extractErrorMessage(err, 'failed to load workers'));
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const persistConfig = async (next: WorkerConfig) => {
    setConfig(await adminApi.updateWorkerConfig(next));
  };

  const replicas = useNumericSetting({
    current: config?.desired_replica_count ?? null,
    min: 1,
    max: 16,
    persist: async (v) => {
      if (config) await persistConfig({ ...config, desired_replica_count: v });
    },
    fallbackError: 'failed to update worker config',
  });

  const apiProcesses = useNumericSetting({
    current: config?.api_worker_processes ?? null,
    min: 1,
    max: 8,
    persist: async (v) => {
      if (config) await persistConfig({ ...config, api_worker_processes: v });
    },
    fallbackError: 'failed to update worker config',
  });

  const onRecycle = async (instanceId: string) => {
    setRecyclingId(instanceId);
    setRecycleError(null);
    try {
      await adminApi.recycleWorker(instanceId);
      // Refresh the live list so the recycled row drops off.
      await loadAll();
    } catch (err) {
      setRecycleError(
        extractErrorMessage(err, `failed to recycle ${instanceId}`),
      );
    } finally {
      setRecyclingId(null);
    }
  };

  const liveCount = (workers ?? []).filter((w) => w.status !== 'down').length;
  const desired = config?.desired_replica_count ?? null;

  return (
    <Stack spacing={3} sx={{ maxWidth: 960 }}>
      {!embedded && (
        <Stack>
          <Typography variant="h4">Workers</Typography>
          <Typography variant="body2" color="text.secondary">
            Insights-worker replica scaling. Setting the desired count
            here records intent; an operator still has to apply the
            change via the deploy command for the running environment.
          </Typography>
        </Stack>
      )}

      {loadError && <Alert severity="error">{loadError}</Alert>}

      {/* Scoring throughput (JOB-TIMINGS) — first, because "how long does
          scoring take, and is it getting worse" is the question people come
          to this page with; the replica settings below are what you change
          once you have an answer. */}
      <JobTimingsCard />

      {/* ---------------- Insights-worker replica count ---------------- */}
      <WorkerSettingCard
        title="Insights-worker replicas"
        fieldLabel="Replicas"
        min={1}
        max={16}
        setting={replicas}
        ready={config !== null}
        loading={!config && !loadError}
        rowSuffix={
          desired !== null ? (
            <Typography variant="body2" color="text.secondary">
              Running {liveCount} of desired {desired}
            </Typography>
          ) : null
        }
        deploy={
          config && (
            <>
              <Typography variant="body2" color="text.secondary">
                After saving, apply the change to the actual deployment:
              </Typography>
              <DeployCommand>
                {`podman-compose up -d --scale insights-worker=${
                  config.desired_replica_count
                }     # local dev
kubectl scale deployment insights-worker --replicas=${
                  config.desired_replica_count
                }   # prod`}
              </DeployCommand>
            </>
          )
        }
      />

      {/* ---------------- API uvicorn process count ---------------- */}
      <WorkerSettingCard
        title="API server processes"
        description="Number of uvicorn processes inside the api pod. More processes let the api handle concurrent requests without one slow handler blocking the others, at the cost of memory per process."
        fieldLabel="Processes"
        min={1}
        max={8}
        setting={apiProcesses}
        ready={config !== null}
        deploy={
          config && (
            <>
              <Typography variant="body2" color="text.secondary">
                After saving, set UVICORN_WORKERS in the deploy env and
                recreate the api container:
              </Typography>
              <DeployCommand>
                {`echo "UVICORN_WORKERS=${
                  config.api_worker_processes
                }" >> infra/.env && cd infra && podman-compose up -d --force-recreate api   # local dev
kubectl set env deployment/api UVICORN_WORKERS=${
                  config.api_worker_processes
                } && kubectl rollout restart deployment/api   # prod`}
              </DeployCommand>
            </>
          )
        }
      />

      {/* ---------------- Live worker replicas ---------------- */}
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Live workers
          </Typography>
          <Divider sx={{ my: 1 }} />

          {recycleError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {recycleError}
            </Alert>
          )}

          {!workers && !loadError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {workers && workers.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No insights-worker replicas reporting yet.
            </Typography>
          )}

          {workers && workers.length > 0 && (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <SortHeader label="Instance" col="instance" sort={sort} />
                  <SortHeader label="Status" col="status" sort={sort} />
                  <SortHeader label="Version" col="version" sort={sort} />
                  <SortHeader label="Last heartbeat" col="heartbeat" sort={sort} />
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedWorkers.map((w) => {
                  const instance = parseInstance(w);
                  return (
                    <TableRow key={w.name}>
                      <TableCell>{instance ?? w.name}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={w.status}
                          color={statusChipColor(w.status)}
                        />
                      </TableCell>
                      <TableCell>{w.version ?? '—'}</TableCell>
                      <TableCell>{w.detail}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="warning"
                          variant="outlined"
                          disabled={instance === null || recyclingId !== null}
                          onClick={() =>
                            instance !== null && void onRecycle(instance)
                          }
                        >
                          {recyclingId === instance ? 'Recycling…' : 'Recycle'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
